import React, { useEffect, useState } from 'react';
import LocalModal from 'components/base/local-modal';
import happyPeople from 'assets/images/happy-people.png';
import { Button, ToastMessage } from '@cokitchen/cokitchen-components';
import { ReactComponent as EditIcon } from 'assets/icons/local-edit-icon.svg';
import { ReactComponent as DollarIcon } from 'assets/icons/dollar-icon.svg';
import { ReactComponent as LocationIcon } from 'assets/icons/location-icon.svg';
import AddressModal from './address-modal';
import { ReactComponent as PeopleIcon } from 'assets/icons/people.svg';

import useDashboardContext from 'contexts/dashboard.context';
import LimitModal from './limit-modal';
import { formatCurrency, getIsBusy, getNearestCokitchen } from 'utils/functions';
import { STORAGE_KEYS } from 'utils/constants';
import AssignMealModal from '../assign-meal';
import GroupOrderService from 'services/group-order.service';
import useOrdersContext from 'contexts/orders.context';
import { CoKitchenType } from 'models/cokitchen.model';
import BusyModal from '../busy-modal';
import { LocationObjectType } from 'models/location.model';
import { toast } from 'react-toastify';

type Props = {
  open: boolean;
  onClose: () => void;
  setShowDeliveryModal: (value: boolean) => void;
};

const NewGroupOrderModal = (props: Props) => {
  const { open, onClose, setShowDeliveryModal } = props;
  const { members } = useDashboardContext();
  const { setCreateGroupOrderObject, createGroupOrderObject } = useOrdersContext();

  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);

  const [showMembersModal, setShowMembersModal] = useState(false);
  const [membersIds, setMembersIds] = useState<string[]>([]);
  const [cokitchen, setCokitchen] = useState<CoKitchenType>();
  const [isCheckingCokitchen, setIsCheckingCokitchen] = useState(false);
  const [showCokitchenIsBusy, setShowCokitchenIsBusy] = useState(false);
  const DEFAULT_PHONE_NUMBER = localStorage.getItem(STORAGE_KEYS.PHONE_NUMBER);
  const COKITCHEN_LEKKI = 'Cokitchen Lekki';

  useEffect(() => {
    setCreateGroupOrderObject((prevState) => ({
      ...prevState,

      company_user_ids: membersIds
    }));
  }, [membersIds]);

  const onAddressConfirm = async (address: LocationObjectType) => {
    const cokitchen: CoKitchenType = await getNearestCokitchen(address, setIsCheckingCokitchen);
    if (cokitchen.name !== COKITCHEN_LEKKI) {
      toast(<ToastMessage type='error' message={'Location not supported'} />);
      onClose();
      return;
    }
    const cokitchenIsBusy = getIsBusy(cokitchen);
    if (cokitchenIsBusy) {
      setCokitchen(cokitchen);
      return setShowCokitchenIsBusy(true);
    }
    setCreateGroupOrderObject((prevState) => ({
      ...prevState,
      address_details: {
        name: address.name,
        address_line: address.address,
        city: address.address.split(',')[1] ?? '',
        phone_number: DEFAULT_PHONE_NUMBER ?? '',
        building_number: address.street_number ?? ''
      },
      lat: address.lat.toString(),
      lng: address.lng.toString()
    }));
    setShowAddressModal(false);
  };

  const close = () => {
    onClose();
  };

  const address = createGroupOrderObject.address_details;
  return (
    <LocalModal isOpen={open} onRequestClose={close} title={'Invite to order'}>
      <div className='flex flex-col gap-14'>
        <div className='flex flex-col gap-6'>
          <article>
            <img src={happyPeople} alt='Happy people' className='w-[129px] h-[129px]' />
            <h3 className='text-xl text-[#2E3031] font-semibold'>Start a new group order</h3>
            <p className='text-sm text-[#6F7376]'>
              Invite your team to order with you, from their own phones
            </p>
          </article>
          <div className='flex flex-col gap-[18px]'>
            <div className='flex items-center gap-3 shrink-0'>
              <LocationIcon />
              <p className='text-sm text-[#6F7376]'>
                Deliver to&nbsp;
                <span className='text-[#2E3031]'>{address.address_line || 'N/A'}</span>
              </p>
              <button
                className='ml-auto p-2 rounded-full cursor-pointer hover:bg-black/10'
                onClick={() => setShowAddressModal(true)}>
                <EditIcon />
              </button>
            </div>
            <div className='flex items-center gap-3'>
              <DollarIcon />
              <p className='text-sm text-[#6F7376]'>
                Spending limit?&nbsp;
                <span className='text-[#2E3031]'>
                  {createGroupOrderObject.group_spend_limit
                    ? formatCurrency(Number(createGroupOrderObject.group_spend_limit))
                    : 'No limit'}
                </span>
              </p>
              <button
                className='ml-auto p-2 rounded-full cursor-pointer hover:bg-black/10'
                onClick={() => setShowLimitModal(true)}>
                <EditIcon />
              </button>
            </div>
            <div className='flex items-center gap-3'>
              <PeopleIcon />
              <p className='text-sm text-[#6F7376]'>Invite members to join</p>
              <button
                onClick={() => setShowMembersModal(true)}
                className='ml-auto p-2 rounded-full cursor-pointer hover:bg-black/10'>
                <EditIcon />
              </button>
            </div>
          </div>
        </div>

        {showLimitModal && <LimitModal onClose={() => setShowLimitModal(false)} />}
        <Button
          small
          variant='block'
          color='primary'
          disabled={
            !createGroupOrderObject.address_details.address_line ||
            createGroupOrderObject.company_user_ids.length === 0
          }
          onClick={() => {
            setShowDeliveryModal(true);
            onClose();
          }}>
          Create group order
        </Button>
      </div>
      <AddressModal
        open={showAddressModal}
        onClose={() => {
          setShowAddressModal(false);
        }}
        onConfirm={onAddressConfirm}
        loading={isCheckingCokitchen}
      />
      {cokitchen ? (
        <BusyModal
          open={showCokitchenIsBusy}
          onClose={() => setShowCokitchenIsBusy(false)}
          data={cokitchen}
        />
      ) : null}
      {showMembersModal && (
        <AssignMealModal
          title='Invite Members'
          show={showMembersModal}
          onClose={() => setShowMembersModal(false)}
          handleClick={(members) => {
            GroupOrderService.AddMember(members, setMembersIds);
          }}
          defaultMembers={GroupOrderService.GetDefaultMembers(
            members,
            createGroupOrderObject.company_user_ids
          )}
        />
      )}
    </LocalModal>
  );
};

export default NewGroupOrderModal;
